/**
 * <nia-filter-drawer> y <nia-facets>
 *
 * El formulario es un GET real contra la URL de la colección: sin JavaScript,
 * el botón de envío recarga la página con los parámetros puestos, que es el
 * comportamiento nativo de los filtros de Shopify. Lo que agrega este archivo
 * es interceptarlo para pedir solo la sección de resultados con la Section
 * Rendering API y actualizar la URL sin recargar.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';

class NiaFilterDrawer extends HTMLElement {
  #lastFocus = null;

  connectedCallback() {
    this.panel = this.querySelector('.nia-tb__panel');
    this.trigger = this.querySelector('[data-nia-tb-open]');
    this.resultsId = this.dataset.results || 'main';

    this.trigger?.addEventListener('click', this.open);
    this.addEventListener('click', this.#onClick);
    this.querySelector('[data-nia-tb-sort]')?.addEventListener('change', this.#onSort);
    document.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    document.body.style.overflow = '';
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open = () => {
    if (this.isOpen) return;
    this.#lastFocus = document.activeElement;
    this.classList.add('is-open');
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.panel?.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    this.panel?.querySelector(FOCUSABLE)?.focus();
  };

  close = () => {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.panel?.setAttribute('inert', '');
    document.body.style.overflow = '';
    if (this.#lastFocus instanceof HTMLElement) this.#lastFocus.focus();
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-nia-tb-close]')) {
      event.preventDefault();
      this.close();
    }
  };

  #onKeydown = (event) => {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab' || !this.panel) return;

    const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** El orden no vive en el form: cambia la URL directo, conservando filtros. */
  #onSort = (event) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', event.target.value);
    url.searchParams.delete('page');
    this.querySelector('nia-facets')?.navigate(url);
  };
}

class NiaFacets extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.drawer = this.closest('nia-filter-drawer');

    this.form?.addEventListener('submit', this.#onSubmit);
    this.form?.addEventListener('change', this.#onChange);
    this.form?.addEventListener('input', this.#onRailInput);
    window.addEventListener('popstate', this.#onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#onPopState);
  }

  /** El botón atrás vuelve a pedir los resultados de la URL anterior. */
  #onPopState = () => {
    this.navigate(new URL(window.location.href), { push: false });
  };

  #onSubmit = (event) => {
    event.preventDefault();
    this.navigate(this.#urlFromForm());
    this.drawer?.close();
  };

  /** Cada casilla aplica al momento; los campos de precio esperan al envío. */
  #onChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === 'number') return;
    this.navigate(this.#urlFromForm());
  };

  /** Mueve el riel indicador mientras se escriben los montos. */
  #onRailInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'number') return;

    const rail = this.querySelector('[data-nia-tb-rail]');
    if (!rail) return;

    const max = Number(rail.dataset.max) / 100;
    if (!max) return;

    const inputs = Array.from(this.querySelectorAll('[data-nia-tb-price]'));
    const from = Number(inputs[0]?.value) || 0;
    const to = Number(inputs[1]?.value) || max;

    rail.style.setProperty('--nia-rail-from', `${Math.max(0, Math.min(100, (from / max) * 100))}%`);
    rail.style.setProperty('--nia-rail-to', `${Math.max(0, Math.min(100, (to / max) * 100))}%`);
  };

  #urlFromForm() {
    const url = new URL(this.form.action, window.location.origin);
    const data = new FormData(this.form);

    for (const [key, value] of data.entries()) {
      if (String(value).trim() !== '') url.searchParams.append(key, value);
    }

    return url;
  }

  /**
   * Pide solo la sección de resultados y la sustituye. El resto de la página
   * ni se toca, así que el panel de filtros conserva su estado y su foco.
   * @param {URL} url
   * @param {{push?: boolean}} [options] push:false al volver con el historial,
   *   para no apilar una entrada por cada paso atrás.
   */
  async navigate(url, options = {}) {
    const push = options.push !== false;
    const drawer = this.drawer;
    const resultsId = drawer?.resultsId ?? 'main';
    const container = document.getElementById(`shopify-section-${resultsId}`);

    drawer?.setAttribute('busy', '');
    document.dispatchEvent(new CustomEvent('nia:facets:start'));

    try {
      const request = new URL(url);
      request.searchParams.set('section_id', resultsId);

      const response = await fetch(request, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`section_id respondió ${response.status}`);

      const html = await response.text();
      if (container) {
        const fresh = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(`#shopify-section-${resultsId}`);
        container.innerHTML = fresh ? fresh.innerHTML : html;
      }

      // pushState y no replaceState: sin entrada nueva, el botón atrás del
      // navegador saltaría fuera de la colección en lugar de deshacer el filtro.
      if (push) history.pushState({ nia: true }, '', url.toString());
      this.#syncCounts();
    } catch (error) {
      // Si algo falla, se cae al comportamiento nativo: navegar de verdad.
      console.warn('[nia-facets] Filtrado sin recarga falló, navegando:', error);
      window.location.assign(url.toString());
    } finally {
      drawer?.removeAttribute('busy');
      document.dispatchEvent(new CustomEvent('nia:facets:end'));
    }
  }

  /** Toma el total que publica la rejilla, no el número de tarjetas pintadas. */
  #syncCounts() {
    const grid = document.querySelector('nia-collection-grid[data-total]');
    const total = Number(grid?.dataset.total);
    if (!Number.isFinite(total)) return;

    const submit = this.querySelector('[data-nia-tb-submit]');
    if (submit) submit.textContent = `${submit.dataset.label ?? 'Ver resultados'} (${total})`;

    const counter = this.drawer?.querySelector('[data-nia-tb-count]');
    if (counter) counter.textContent = counter.textContent.replace(/^\s*\d+/, String(total));
  }
}

if (!customElements.get('nia-filter-drawer')) {
  customElements.define('nia-filter-drawer', NiaFilterDrawer);
}

if (!customElements.get('nia-facets')) {
  customElements.define('nia-facets', NiaFacets);
}
